import React, { useEffect } from 'react';
import { X, MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { Language } from '../types';
import { CONTENT } from '../constants';

interface LocationDetailProps {
  onClose: () => void;
  lang: Language;
}

export const LocationDetail: React.FC<LocationDetailProps> = ({ onClose, lang }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const t = CONTENT[lang];
  const isRTL = lang === 'ar';

  return (
    <div className="fixed inset-0 z-[60] bg-luxury-paper overflow-y-auto animate-fade-in" dir={isRTL ? 'rtl' : 'ltr'}>
       {/* Close Button */}
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-50 p-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white hover:bg-white hover:text-black transition-all duration-300 group shadow-lg"
      >
        <X size={24} strokeWidth={1} className="group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Hero / Map Header */}
      <div className="relative h-[50vh] w-full overflow-hidden bg-stone-900">
        {/* Placeholder for map - styled to look professional */}
        <div className="absolute inset-0 opacity-60">
            <img 
                src="https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?q=80&w=2662&auto=format&fit=crop"
                className="w-full h-full object-cover grayscale"
                alt="Map Background"
            />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-luxury-paper via-transparent to-black/30"></div>

        <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 max-w-[1920px] mx-auto">
             <div className="animate-fade-up">
                <span className="text-greenline-600 text-xs uppercase tracking-[0.3em] font-medium block mb-4 bg-white/80 backdrop-blur-md px-4 py-2 w-fit rounded-full">
                  {lang === 'ar' ? 'شبكة الفروع' : 'Global Network'}
                </span>
                <h1 className="font-serif text-5xl md:text-7xl text-luxury-black mb-2">
                  {lang === 'ar' ? 'مواقعنا' : 'Our Locations'}
                </h1>
             </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1920px] mx-auto px-6 md:px-16 py-16">
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* List of Offices */}
            <div className="space-y-12">
               <div className="group border-l-2 border-greenline-900 pl-8 transition-all">
                  <h2 className="font-serif text-3xl text-luxury-black mb-6">
                    {lang === 'ar' ? 'المقر الرئيسي - بنغازي' : 'Benghazi Headquarters'}
                  </h2>
                  
                  <div className="space-y-6 text-lg font-light text-stone-600">
                     <div className="flex items-start gap-4">
                        <MapPin className="mt-1 text-greenline-600 shrink-0" size={20} />
                        <p className="leading-relaxed max-w-md">
                           {t.contact.address}
                        </p>
                     </div>
                     <div className="flex items-center gap-4">
                        <Phone className="text-greenline-600 shrink-0" size={20} />
                        <a href={`tel:${t.contact.phone}`} className="hover:text-greenline-900 transition-colors" dir="ltr">{t.contact.phone}</a>
                     </div>
                     <div className="flex items-center gap-4">
                        <Mail className="text-greenline-600 shrink-0" size={20} />
                        <a href={`mailto:${t.contact.email}`} className="hover:text-greenline-900 transition-colors">{t.contact.email}</a>
                     </div>
                     <div className="flex items-start gap-4">
                        <Clock className="mt-1 text-greenline-600 shrink-0" size={20} />
                        <div>
                            <p>{lang === 'ar' ? 'الأحد - الخميس: ٩:٠٠ ص - ٥:٠٠ م' : 'Sunday - Thursday: 9:00 AM - 5:00 PM'}</p>
                            <p className="text-stone-400 text-sm">{lang === 'ar' ? 'الجمعة - السبت: مغلق' : 'Friday - Saturday: Closed'}</p>
                        </div>
                     </div>
                  </div>

                  <div className="mt-10">
                     <a 
                        href="https://www.google.com/maps/search/?api=1&query=Al-Madina+Tower+Benghazi+Libya" 
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center gap-3 px-8 py-4 bg-luxury-black text-white text-xs uppercase tracking-[0.2em] hover:bg-greenline-900 transition-colors group/btn"
                     >
                        {lang === 'ar' ? 'احصل على الاتجاهات' : 'Get Directions'}
                        <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform rtl:group-hover/btn:-translate-x-1 rtl:rotate-180" />
                     </a>
                  </div>
               </div>
            </div>

            {/* Visual Map Detail (Interactive-like) */}
            <div className="h-[500px] bg-stone-100 relative overflow-hidden group rounded-lg border border-stone-200">
               {/* Static map image for Benghazi */}
               <img 
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=2674&auto=format&fit=crop" 
                  alt="Benghazi Location" 
                  className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
               />
               <div className="absolute top-[53%] left-[53%] -translate-x-1/2 -translate-y-1/2">
                  <div className="relative">
                     <div className="w-4 h-4 bg-greenline-500 rounded-full animate-ping absolute inset-0"></div>
                     <div className="w-4 h-4 bg-greenline-600 rounded-full relative shadow-xl border-2 border-white"></div>
                  </div>
               </div>
               <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur p-4 max-w-xs shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-widest text-luxury-black mb-1">Benghazi Port</p>
                  <p className="text-[10px] text-stone-500">Strategic proximity to major shipping terminals.</p>
               </div>
            </div>

         </div>
      </div>
    </div>
  );
};
